import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { SideNavItem } from './side-nav-item';
import { getFeatureComponentPath, getPathsWithFallback, hasPageComponent } from '@utils/shared/route-generator';

const renderItems = ({ depth = 0, item, pathname, path, parentPath = '/admin', parentFeatureHandle = null }) => {
  const node = item.node || {}
  const children = item.children || [];
  
  // Early return if path is undefined or null
  if (!path) {
    return null;
  }
  
  // Convert kebab-case handle to camelCase for path lookup
  const toCamelCase = (str) => {
    return str.replaceAll(/-([a-z])/g, (match, letter) => letter.toUpperCase());
  };
  
  const parentHandle = node.handle;
  const pathKey = toCamelCase(parentHandle);
  
  // Early return if this path doesn't exist in the paths object
  if (!path[pathKey]) {
    return null;
  }
  
  const itemPath = path[pathKey]?.index;
  const checkPath = !!(itemPath && pathname);
  
  // Fix: Use proper path matching instead of includes() to avoid false positives
  // Check if pathname starts with the item path (for partial match in parent items)
  const partialMatch = checkPath ? pathname.startsWith(itemPath) : false;
  // Exact match for leaf items - check if paths match exactly or if it's a detail/add route
  const exactMatch = checkPath ? (
    pathname === itemPath || 
    pathname.startsWith(`${itemPath}/`)
  ) : false;

  if (path[pathKey]) {
    if (children && children.length > 0) {
      // A parent node only gets its own clickable path when it actually has a
      // page.jsx (e.g. "apps" with "nlv" nested under it) — most parents are
      // pure grouping folders with no page of their own, and would 404 if
      // pointed at `itemPath` (which the paths object sets regardless).
      const componentPath = getFeatureComponentPath(parentPath, node, parentFeatureHandle);
      const ownPagePath = hasPageComponent(componentPath) ? itemPath : undefined;

      return (<SideNavItem
          active={partialMatch}
          depth={depth}
          disabled={node.disabled}
          icon={node.icon}
          key={node.handle}
          label={node.label}
          open={partialMatch}
          path={ownPagePath}
          title={node.handle}
        >
          <Stack
            component="ul"
            spacing={0.5}
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
            }}
          >
            {children.map((childItem) => renderItems({
              depth: depth + 1,
              item: childItem,
              pathname,
              path: path[pathKey],
              parentPath: `${parentPath}/${node.handle}`,
              parentFeatureHandle: node.handle,
            }))}
          </Stack>
        </SideNavItem>
      )
    } else {
      return (<SideNavItem
        active={exactMatch}
        depth={depth}
        disabled={node.disabled}
        external={node.external}
        icon={node.icon}
        key={node.handle}
        label={node.label}
        path={path[pathKey]?.index}
        title={node.handle}
      />)
    }
  }
};

export const SideNavSection = (props) => {
  const { items = [], pathname, subheader = '', ...other } = props;
  const paths = getPathsWithFallback();

  // Don't render if paths haven't loaded yet
  if (!paths?.admin) {
    return null;
  }

  return (
    <Stack
      component="ul"
      spacing={0.5}
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
      }}
      {...other}
    >
      {subheader && (
        <Box
          component="li"
          sx={{
            color: 'var(--nav-section-title-color)',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.66,
            mb: 1,
            ml: 1,
            textTransform: 'uppercase',
          }}
        >
          {subheader}
        </Box>
      )}
      { items.length > 0 &&  items.map((item) => renderItems({ item, pathname, path: paths.admin })) }
    </Stack>
  );
};

SideNavSection.propTypes = {
  items: PropTypes.array,
  pathname: PropTypes.string,
  subheader: PropTypes.string,
};
